'use client';

import { useEffect } from 'react';
import { Button } from '@mokoboko/components';

import { useInstallPlatform } from '@/hooks/use-install-platform';
import { useStoredDismissal } from '@/hooks/use-stored-dismissal';
import { AnalyticsEvent, track } from '@/lib/analytics/tracker';
import { openInstallHelp } from '@/lib/home-screen-app-store';
import { APPLE_TOUCH_ICON_PATH, isPhonePlatform } from '@/lib/install-platform';
import useTenant from '@/state/tenant';

const STORAGE_KEY = 'mokoboko:install-card:snoozed-at:v1';
// Snoozed, not dismissed for good: a visitor who said "Not now" after one
// booking may want the app a month later.
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

// Offered after a booking, the moment a visitor has a reason to come back.
// Phones only: a computer's install is in the account menu.
export const InstallAppCard = () => {
  const platform = useInstallPlatform();
  const [snoozed, snooze] = useStoredDismissal(STORAGE_KEY, SNOOZE_MS);
  const { data: tenant } = useTenant();
  const visible = !!platform && isPhonePlatform(platform) && !snoozed;

  useEffect(() => {
    if (visible) {
      track({
        event: AnalyticsEvent.INSTALL_CARD_SHOWN,
        properties: { platform },
      });
    }
  }, [visible, platform]);

  if (!visible) return null;

  return (
    <div className="flex gap-4 rounded-xl bg-gray-50 p-4">
      <img
        src={APPLE_TOUCH_ICON_PATH}
        alt=""
        width={48}
        height={48}
        className="size-12 shrink-0 rounded-[11px] shadow-sm"
      />
      <div className="flex flex-1 flex-col gap-3">
        <div>
          <p className="text-base font-semibold leading-6 text-gray-900">
            Book faster next time
          </p>
          <p className="mt-0.5 text-sm leading-5 text-gray-700">
            Add {tenant?.name ?? 'this studio'} to your Home Screen and open it
            like an app.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="tertiary-gray"
            size="md"
            onClick={() => {
              track({
                event: AnalyticsEvent.INSTALL_CARD_DISMISSED,
                properties: { platform },
              });
              snooze();
            }}
          >
            Not now
          </Button>
          <Button
            variant="secondary-gray"
            size="md"
            onClick={() => {
              snooze();
              openInstallHelp('booking_receipt');
            }}
          >
            Show me how
          </Button>
        </div>
      </div>
    </div>
  );
};
